// iter619 — Passive Stress Detection banner.
// iter620 — Adds Relaxation Plate CTA (Active Recovery Coach) — when stress is
// detected, user can preview a curated anti-cortisol plate and 1-tap log it.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import RelaxationPlateModal from "@/components/dashboard/RelaxationPlateModal";
import CortisolThyroidAxisCard from "@/components/dashboard/CortisolThyroidAxisCard";

export default function PassiveStressBanner() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [plateOpen, setPlateOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchDetect = async () => {
      try {
        const { data: r } = await axios.get(`${API}/stress-twin/detect-passive`);
        if (!cancelled) setData(r);
      } catch {
        /* silent */
      }
    };
    fetchDetect();
    // iter623 — Re-fetch when thyroid lab is updated elsewhere (Cortisol-Thyroid
    // axis depends on TSH). Also listen for stress/meal log changes.
    const handler = () => fetchDetect();
    window.addEventListener("thyroid-lab-changed", handler);
    window.addEventListener("stress-log-changed", handler);
    return () => {
      cancelled = true;
      window.removeEventListener("thyroid-lab-changed", handler);
      window.removeEventListener("stress-log-changed", handler);
    };
  }, [API]);

  // iter622 — axis card can appear independently of the stress banner
  const axisCard = data?.axis_check?.detected ? (
    <CortisolThyroidAxisCard axis={data.axis_check} defaultCollapsed={true} />
  ) : null;

  // Early return ONLY if BOTH stress banner conditions AND axis are unmet
  const showStressBanner = data && data.detected
    && !data.already_predicted_today
    && !data.user_has_manual_today
    && !hidden;
  if (!showStressBanner && !axisCard) {
    return null;
  }

  const accept = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/stress-twin/confirm-passive`, {
        accept: true,
        triggers: data.triggers,
        predicted_1_10: data.predicted_1_10,
      });
      toast.success("Записахме предположението — благодарим, че го потвърди.");
      setHidden(true);
      // Refresh stress tile
      window.dispatchEvent(new Event("stress-log-changed"));
    } catch {
      toast.error("Неуспешно записване. Опитай отново.");
    } finally {
      setSubmitting(false);
    }
  };

  const dismiss = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/stress-twin/confirm-passive`, { accept: false });
      setHidden(true);
    } catch {
      setHidden(true);
    } finally {
      setSubmitting(false);
    }
  };

  const highSeverity = (data.triggers || []).some((t) => t.severity === "high");
  const baseCls = isDark
    ? "bg-gradient-to-br from-orange-900/30 to-rose-900/20 border-orange-500/30 text-orange-100"
    : "bg-gradient-to-br from-orange-50 to-rose-50 border-orange-200 text-orange-900";

  return (
    <div className="space-y-3">
      {showStressBanner && (
        <section
          data-testid="passive-stress-banner"
          className={`rounded-2xl border p-4 sm:p-5 ${baseCls}`}
        >
          <div className="flex items-start gap-3">
            <span className="text-2xl shrink-0" aria-hidden="true">⚡</span>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold uppercase tracking-wider mb-1" data-testid="passive-stress-title">
                {highSeverity ? "Тялото ти показва признаци на стрес" : "Може би си под лек стрес"}
              </h3>
              <p className="text-sm leading-relaxed mb-2" data-testid="passive-stress-summary">
                {data.summary_bg}
              </p>
              <ul className="text-xs space-y-1 mb-3 opacity-90" data-testid="passive-stress-triggers">
                {(data.triggers || []).map((t, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`passive-stress-trigger-${t.type}`}>
                    <span aria-hidden="true">{t.severity === "high" ? "🔴" : "🟡"}</span>
                    <span>{t.label_bg}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] opacity-80 mb-3">
                Можеш да потвърдиш това усещане, за да го запишем като ниво {data.predicted_1_10}/10
                в Stress Twin, или да го отхвърлиш.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={accept}
                  data-testid="passive-stress-accept"
                  className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${
                    isDark
                      ? "bg-orange-500/30 text-orange-100 hover:bg-orange-500/40"
                      : "bg-orange-200 text-orange-900 hover:bg-orange-300"
                  } disabled:opacity-50`}
                >
                  ✓ Потвърди ({data.predicted_1_10}/10)
                </button>
                {/* iter620 — Active Recovery Coach CTA */}
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => setPlateOpen(true)}
                  data-testid="passive-stress-relaxation-plate"
                  className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${
                    isDark
                      ? "bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30"
                      : "bg-emerald-100 text-emerald-900 hover:bg-emerald-200"
                  } disabled:opacity-50`}
                >
                  🍽️ Покажи Relaxation чиния
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={dismiss}
                  data-testid="passive-stress-dismiss"
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                    isDark
                      ? "text-orange-200 hover:text-orange-50"
                      : "text-orange-700 hover:text-orange-900"
                  } disabled:opacity-50`}
                >
                  Не сега
                </button>
              </div>
            </div>
          </div>
          <RelaxationPlateModal
            open={plateOpen}
            onClose={() => setPlateOpen(false)}
            onLogged={() => window.dispatchEvent(new Event("meal-log-changed"))}
          />
        </section>
      )}
      {/* iter622 — Cortisol-Thyroid Axis circuit-level coaching */}
      {axisCard}
    </div>
  );
}
